import { CheckCircleIcon } from "@heroicons/react/24/outline";
import {
    BusinessProfileDetails,
    businessProfileFormatProblems,
    BusinessProfileId,
    businessProfileProgress,
    BusinessProfileStep,
    businessProfileStepProblem,
    businessProfileSteps,
    completedBusinessProfileSteps,
    RegionId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { Breadcrumbs } from "components/ui/breadcrumbs";
import { SecondaryButtonLink } from "components/ui/button-link";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { AutosaveStatus, useDraftAutosave } from "hooks/use-draft-autosave";
import { usePreventUnload } from "hooks/use-prevent-unload";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useIsMultiRegionUser } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { BusinessProfileStepper } from "./business-profile-stepper";
import {
    businessDisplayName,
    businessVerificationPath,
    toBusinessProfileDetails,
} from "./business-profile-types";
import { ProgressBadge, readyForReviewText } from "./business-verification-cards";
import { StepAddress } from "./step-address";
import { BusinessOffices, StepBusiness } from "./step-business";
import { StepRepresentative } from "./step-representative";
import {
    BusinessProfileOutput,
    useBusinessProfiles,
    useOfficeName,
    useRefreshBusinessProfiles,
    useStoreSavedBusinessProfile,
} from "./use-business-verification";
import { WizardStepProps } from "./wizard-step-props";

const autosaveDelayMs = 1000;
const lastStepIndex = businessProfileSteps.length - 1;

const firstOpenStep = (details: BusinessProfileDetails): BusinessProfileStep =>
    businessProfileSteps.find(
        step => businessProfileStepProblem(details, step) !== null,
    ) ?? businessProfileSteps[0];

const SaveStatus: React.FC<{ status: AutosaveStatus }> = ({ status }) => {
    if (status === "failed") {
        return (
            <span className="text-xs text-red-600">Couldn&apos;t save your changes</span>
        );
    }
    if (status === "saved") {
        return (
            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                <CheckCircleIcon className="size-4" aria-hidden /> All changes saved
            </span>
        );
    }
    return <span className="text-xs text-gray-500">Saving…</span>;
};

const useOffices = (
    profile: BusinessProfileOutput,
    uncoveredOfficeIds: RegionId[],
): BusinessOffices => {
    const trpcClient = trpc.useTRPC();
    const refresh = useRefreshBusinessProfiles();
    const officeName = useOfficeName();
    const isMultiOffice = useIsMultiRegionUser();
    const updateOffices = useMutation(
        trpcClient.businessProfile.updateOffices.mutationOptions(),
    );
    const [pendingOfficeIds, setPendingOfficeIds] = useState<RegionId[] | null>(null);
    const [failed, setFailed] = useState(false);
    const officeIds = pendingOfficeIds ?? profile.officeIds;

    const changeOffices = async (nextOfficeIds: RegionId[]) => {
        setPendingOfficeIds(nextOfficeIds);
        setFailed(false);
        try {
            await updateOffices.mutateAsync({ id: profile.id, officeIds: nextOfficeIds });
            await refresh();
        } finally {
            setPendingOfficeIds(null);
        }
    };

    const availableOfficeIds = [...profile.officeIds, ...uncoveredOfficeIds].sort(
        (a, b) => officeName(a).localeCompare(officeName(b)),
    );

    return {
        isMultiOffice,
        officeIds,
        availableOfficeIds,
        officeName,
        updating: updateOffices.isPending,
        failed,
        addOffice: regionId => {
            changeOffices([...officeIds, regionId]).catch(() => setFailed(true));
        },
        removeOffice: regionId => {
            changeOffices(officeIds.filter(id => id !== regionId)).catch(() =>
                setFailed(true),
            );
        },
    };
};

interface WizardFormProps {
    profile: BusinessProfileOutput;
    uncoveredOfficeIds: RegionId[];
}

const WizardForm: React.FC<WizardFormProps> = ({ profile, uncoveredOfficeIds }) => {
    const trpcClient = trpc.useTRPC();
    const history = useHistory();
    const refresh = useRefreshBusinessProfiles();
    const storeSaved = useStoreSavedBusinessProfile();
    const offices = useOffices(profile, uncoveredOfficeIds);
    const [details, setDetails] = useState(() => toBusinessProfileDetails(profile));
    const [step, setStep] = useState(() => firstOpenStep(details));

    const saveDraft = useMutation(trpcClient.businessProfile.saveDraft.mutationOptions());
    const save = useCallback(
        async (value: BusinessProfileDetails) => {
            const saved = await saveDraft.mutateAsync({
                id: profile.id,
                details: value,
            });
            storeSaved(saved);
        },
        [saveDraft, storeSaved, profile.id],
    );
    const autosave = useDraftAutosave(details, save, autosaveDelayMs);
    usePreventUnload(autosave.status !== "saved", () => {
        autosave.flush().catch((): void => undefined);
    });

    const goToStep = (next: BusinessProfileStep) => {
        autosave.flush().catch((): void => undefined);
        setStep(next);
        window.scrollTo({ top: 0 });
    };

    const saveAndClose = async () => {
        await autosave.flush();
        handleEvent("business-verification-save-and-close", { step });
        await refresh();
        history.push(businessVerificationPath);
        window.scrollTo({ top: 0 });
    };

    const onSaveAndClose = () => {
        saveAndClose().catch((): void => undefined);
    };

    const stepProps: WizardStepProps = {
        details,
        problems: businessProfileFormatProblems(details),
        onChange: (changes: Partial<BusinessProfileDetails>) =>
            setDetails(current => ({ ...current, ...changes })),
    };

    const stepIndex = businessProfileSteps.indexOf(step);
    const stepProblem = businessProfileStepProblem(details, step);
    const progress = businessProfileProgress(profile.status, details);

    return (
        <ContentFull>
            <div className="space-y-4">
                <div>
                    <Breadcrumbs
                        links={[
                            {
                                label: "SMS & Voice Business Verification",
                                to: businessVerificationPath,
                            },
                        ]}
                        current={businessDisplayName(details)}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
                        <ContentHeader>
                            <span className="inline-flex items-center gap-2">
                                Verify your business
                                <ProgressBadge progress={progress} />
                            </span>
                        </ContentHeader>
                        <SaveStatus status={autosave.status} />
                    </div>
                </div>
                {autosave.status === "failed" ? (
                    <RetryableErrorBanner
                        message="We couldn't save your latest changes. Check your connection, then try again."
                        retrying={false}
                        onRetry={() => {
                            autosave.flush().catch((): void => undefined);
                        }}
                    />
                ) : null}
                <div className="max-w-3xl">
                    <BusinessProfileStepper
                        currentStep={step}
                        completedSteps={completedBusinessProfileSteps(details)}
                        onSelect={goToStep}
                    />
                </div>
                <div className="max-w-3xl">
                    {step === "business" ? (
                        <StepBusiness {...stepProps} offices={offices} />
                    ) : null}
                    {step === "address" ? <StepAddress {...stepProps} /> : null}
                    {step === "representative" ? (
                        <StepRepresentative {...stepProps} />
                    ) : null}
                </div>
                {stepIndex === lastStepIndex && progress === "readyForReview" ? (
                    <div className="max-w-3xl">
                        <InlineBanner type="success">{readyForReviewText}</InlineBanner>
                    </div>
                ) : null}
                <div className="max-w-3xl flex flex-wrap items-start justify-between gap-2 pt-2">
                    <div>
                        {stepIndex > 0 ? (
                            <SecondaryButton
                                type="button"
                                onClick={() =>
                                    goToStep(businessProfileSteps[stepIndex - 1])
                                }>
                                Back
                            </SecondaryButton>
                        ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            {stepIndex === lastStepIndex ? (
                                <PrimaryButton type="button" onClick={onSaveAndClose}>
                                    Save and close
                                </PrimaryButton>
                            ) : (
                                <>
                                    <SecondaryButton
                                        type="button"
                                        onClick={onSaveAndClose}>
                                        Save and close
                                    </SecondaryButton>
                                    <PrimaryButton
                                        type="button"
                                        disabled={!!stepProblem}
                                        title={stepProblem ?? undefined}
                                        onClick={() =>
                                            goToStep(businessProfileSteps[stepIndex + 1])
                                        }>
                                        Continue &rarr;
                                    </PrimaryButton>
                                </>
                            )}
                        </div>
                        {stepProblem ? (
                            <p className="text-xs text-amber-700 text-right">
                                {stepProblem}
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        </ContentFull>
    );
};

const Unavailable: React.FC<{ message: string }> = ({ message }) => (
    <ContentFull>
        <div className="space-y-4">
            <InlineBanner type="info">{message}</InlineBanner>
            <SecondaryButtonLink to={businessVerificationPath}>
                Back to SMS &amp; Voice Business Verification
            </SecondaryButtonLink>
        </div>
    </ContentFull>
);

interface BusinessVerificationWizardProps {
    businessProfileId: BusinessProfileId;
}

export const BusinessVerificationWizard: React.FC<BusinessVerificationWizardProps> = ({
    businessProfileId,
}) => {
    const { profilesQuery, uncoveredOfficesQuery } = useBusinessProfiles();
    const profile = useMemo(
        () => profilesQuery.data?.find(p => p.id === businessProfileId) ?? null,
        [profilesQuery.data, businessProfileId],
    );

    if (profilesQuery.isLoading || uncoveredOfficesQuery.isLoading) {
        return <Spinner />;
    }
    if (profilesQuery.isLoadingError || uncoveredOfficesQuery.isLoadingError) {
        return (
            <ContentFull>
                <RetryableErrorBanner
                    message="We couldn't load this business."
                    retrying={
                        profilesQuery.isFetching || uncoveredOfficesQuery.isFetching
                    }
                    onRetry={() => {
                        profilesQuery.refetch().catch((): void => undefined);
                        uncoveredOfficesQuery.refetch().catch((): void => undefined);
                    }}
                />
            </ContentFull>
        );
    }
    if (!profile) {
        return <Unavailable message="We couldn't find this business." />;
    }
    if (!profile.editable) {
        return (
            <Unavailable message="Only someone who manages every office this business covers can change it." />
        );
    }
    if (profile.status !== "draft") {
        return (
            <Unavailable message="This business has been sent to carriers, so its details can't be changed." />
        );
    }
    return (
        <WizardForm
            key={profile.id}
            profile={profile}
            uncoveredOfficeIds={uncoveredOfficesQuery.data ?? []}
        />
    );
};
